import { useEffect, useState } from "preact/hooks";
import type { ComponentChildren, JSX } from "preact";
import { api, type Site } from "../../api";
import { bytes, dateTime, storageUse } from "../../lib/format";
import { useI18n } from "../../lib/i18n";
import { FeatureHint, PlanLimitNotice, useFeature, usePlanLimit } from "../../components/PlanNotice";
import { CopyBox, ReadOnlyNote, SettingRow, StatusLine, Toggle, useSites, useStatus } from "./common";
import { needsOrigins } from "../../lib/sites";
import { useDialogs } from "../../components/Dialog";

/**
 * Sites and their keys. The one tab where a mistake is expensive, so the
 * dangerous actions — showing a secret, rotating a key, cutting off an old
 * one — say plainly what just happened and what to do next.
 *
 * A site's settings come in five groups, always in the same order — what it
 * is, how it is installed, its secrets, how it is protected, and deleting it —
 * so an admin looking for one thing knows where it lives. Each setting is a
 * row: what it is and why on the left, the control on the right.
 *
 * Each site has two secrets, and both are handled by the same component:
 * the identity key (the site signs its members with it) and the webhook key
 * (we sign ticket requests to the site with it). Rotating either offers the
 * routine path, where the old value keeps working for a day, and the "it
 * leaked" path, where it stops at once.
 */

function parseOrigins(value: string): string[] {
  return value
    .split(/[\n,]/)
    .map((o) => o.trim())
    .filter(Boolean);
}

type Revealed = {
  title: string;
  items: Array<{ label: string; value: string }>;
  warn: string;
};
type SectionId = "general" | "install" | "keys" | "protection" | "danger";
export function SitesTab({ isAdmin, isOwner }: { isAdmin: boolean; isOwner: boolean }): JSX.Element {
  const { t: all, lang } = useI18n();
  const t = all.sites;
  const { sites, reload, error } = useSites();
  const dialogs = useDialogs();
  const [revealed, setRevealed] = useState<Revealed | null>(null);
  const [creating, setCreating] = useState(false);
  const [status, show, fail] = useStatus();
  const { limit, catchLimit, clearLimit } = usePlanLimit();
  // Below Business the server refuses to turn required identity on, so the
  // switch says why instead of failing after the click.
  const canRequireIdentity = useFeature("verifiedIdentity");
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const selected = sites.find((s) => s.id === selectedId) ?? sites[0] ?? null;

  /** Plan refusals get the shared upgrade notice; everything else the usual line. */
  function report(err: unknown): void {
    if (!catchLimit(err)) fail(err);
  }

  async function createSite(name: string, origins: string): Promise<boolean> {
    try {
      clearLimit();
      const res = await api.createSite({
        name: name.trim(),
        allowedOrigins: parseOrigins(origins),
      });
      setRevealed({
        title: t.revealed(res.site.name),
        items: [
          { label: t.secretKey, value: res.secretKey },
          { label: t.webhookSecret, value: res.webhookSecret },
        ],
        // A site created without a domain is embedded, reloaded, and shows
        // nothing — said here rather than left for them to discover.
        warn: t.secretWarn + t.webhookWarn + (needsOrigins(res.site) ? " " + t.noOriginsWarn : ""),
      });
      await reload();
      setSelectedId(res.site.id);
      return true;
    } catch (err) {
      report(err);
      return false;
    }
  }

  async function saveName(site: Site, value: string): Promise<void> {
    const next = value.trim();
    // An emptied box is a slip, not a rename: the old name simply comes back.
    if (!next || next === site.name) return;
    try {
      await api.updateSite(site.id, { name: next });
      show("ok", t.nameSaved(next));
      await reload();
    } catch (err) {
      report(err);
    }
  }

  async function setActive(site: Site, on: boolean): Promise<void> {
    try {
      clearLimit();
      await api.updateSite(site.id, { isActive: on });
      show("ok", t.activeSaved(site.name, on));
      await reload();
    } catch (err) {
      report(err);
    }
  }

  async function saveRetention(site: Site, value: string): Promise<void> {
    const text = value.trim();
    if (text === String(site.retentionDays)) return;
    const days = /^\d+$/.test(text) ? Number(text) : NaN;
    if (!Number.isInteger(days) || days < 0 || days > 3650) {
      show("error", t.retentionInvalid);
      return;
    }
    try {
      await api.updateSite(site.id, { retentionDays: days });
      show("ok", t.retentionSaved(site.name));
      await reload();
    } catch (err) {
      report(err);
    }
  }

  async function removeSite(site: Site): Promise<void> {
    const sure = await dialogs.confirm({
      title: t.deleteConfirmTitle(site.name),
      body: t.deleteIntro,
      confirmLabel: t.deleteButton,
      danger: true,
      typeToConfirm: site.name,
    });
    if (!sure) return;
    try {
      await api.deleteSite(site.id, site.name);
      show("ok", t.deleted(site.name));
      setSelectedId(null);
      await reload();
    } catch (err) {
      report(err);
    }
  }

  async function saveOrigins(site: Site, value: string): Promise<void> {
    const next = parseOrigins(value);
    if (next.join("\n") === site.allowedOrigins.join("\n")) return;
    try {
      await api.updateSite(site.id, { allowedOrigins: next });
      show("ok", t.originsSaved(site.name));
      await reload();
    } catch (err) {
      report(err);
    }
  }

  async function saveWebhook(site: Site, value: string): Promise<void> {
    const next = value.trim() || null;
    if (next === site.ticketWebhookUrl) return;
    try {
      await api.updateSite(site.id, { ticketWebhookUrl: next });
      show("ok", t.ticketUrlSaved(site.name));
      await reload();
    } catch (err) {
      report(err);
    }
  }

  async function rotate(site: Site, kind: "identity" | "webhook", leaked: boolean): Promise<void> {
    const question =
      kind === "identity"
        ? leaked
          ? t.rotateLeakedConfirm(site.name)
          : t.rotateConfirm(site.name)
        : leaked
          ? t.webhookLeakedConfirm(site.name)
          : t.webhookRotateConfirm(site.name);
    const sure = await dialogs.confirm({
      title: kind === "identity" ? t.secretKey : t.webhookSecret,
      body: question,
      confirmLabel: leaked ? t.rotateLeaked : t.rotate,
      danger: leaked,
    });
    if (!sure) return;
    try {
      const secret =
        kind === "identity"
          ? (await api.rotateKey(site.id, leaked)).secretKey
          : (await api.rotateWebhookSecret(site.id, leaked)).webhookSecret;
      // A first webhook key (a site from before they existed) replaced nothing,
      // so there is no old key to warn about.
      const hadPrevious = kind === "identity" || site.webhookSecretHint !== null;
      const after = !hadPrevious
        ? ""
        : leaked
          ? kind === "identity"
            ? t.revokedWarn
            : t.webhookRevokedWarn
          : t.rotatedWarn;
      setRevealed({
        title: kind === "identity" ? t.rotated(site.name) : t.webhookRotated(site.name),
        items: [
          {
            label: kind === "identity" ? t.secretKey : t.webhookSecret,
            value: secret,
          },
        ],
        warn: t.secretWarn + (kind === "webhook" ? t.webhookWarn : "") + after,
      });
      await reload();
    } catch (err) {
      report(err);
    }
  }

  async function revokePrevious(site: Site, kind: "identity" | "webhook"): Promise<void> {
    const sure = await dialogs.confirm({
      title: t.revokePrevious,
      body: kind === "identity" ? t.revokePreviousConfirm : t.webhookRevokePreviousConfirm,
      confirmLabel: t.revokePrevious,
      danger: true,
    });
    if (!sure) return;
    try {
      if (kind === "identity") await api.revokePreviousKey(site.id);
      else await api.revokePreviousWebhookSecret(site.id);
      show("ok", t.previousRevoked(site.name));
      await reload();
    } catch (err) {
      report(err);
    }
  }

  /** The five groups of one site, each ready to be laid out however the page wants. */
  function sectionsOf(site: Site): Array<{
    id: SectionId;
    title: string;
    intro?: string;
    body: JSX.Element;
  }> {
    const groups: Array<{
      id: SectionId;
      title: string;
      intro?: string;
      body: JSX.Element;
    }> = [
      {
        id: "general",
        title: t.tabGeneral,
        body: (
          <>
            <SettingRow title={t.name} hint={t.nameHint} htmlFor={`name-${site.id}`}>
              <input
                id={`name-${site.id}`}
                type="text"
                // Keyed on the saved name so a reload after saving shows it, while
                // typing is left alone until the box is left.
                key={site.name}
                defaultValue={site.name}
                maxLength={120}
                disabled={!isAdmin}
                onBlur={(e) => void saveName(site, e.currentTarget.value)}
              />
            </SettingRow>
            <SettingRow title={t.active} hint={t.activeHint}>
              <Toggle
                checked={site.isActive}
                disabled={!isAdmin}
                label={t.active}
                onChange={(on) => void setActive(site, on)}
              />
            </SettingRow>
            <SettingRow title={t.retention} hint={t.retentionHint} htmlFor={`retention-${site.id}`}>
              <div class="suffix-field">
                <input
                  id={`retention-${site.id}`}
                  type="number"
                  min={0}
                  max={3650}
                  key={site.retentionDays}
                  defaultValue={String(site.retentionDays)}
                  disabled={!isAdmin}
                  onBlur={(e) => void saveRetention(site, e.currentTarget.value)}
                />
                <span>{t.days}</span>
              </div>
            </SettingRow>
            <StorageRow storage={site.storage} lang={lang} />
          </>
        ),
      },
      {
        id: "install",
        title: t.tabInstall,
        intro: t.installIntro,
        body: (
          <>
            {needsOrigins(site) ? <div class="alert warn">{t.noOriginsWarn}</div> : null}
            <SettingRow title={t.embed} hint={t.embedHint} wide>
              <CopyBox value={site.embedSnippet} />
            </SettingRow>
            <SettingRow title={t.origins} hint={t.originsHint} htmlFor={`origins-${site.id}`}>
              <textarea
                id={`origins-${site.id}`}
                rows={Math.max(2, site.allowedOrigins.length + 1)}
                value={site.allowedOrigins.join("\n")}
                placeholder="https://siteniz.com"
                disabled={!isAdmin}
                onBlur={(e) => void saveOrigins(site, e.currentTarget.value)}
              />
            </SettingRow>
            <SettingRow title={t.publicKey} hint={t.publicKeyHint}>
              <CopyBox value={site.publicKey} />
            </SettingRow>
            <SettingRow title={t.ticketUrl} hint={t.ticketUrlHint} htmlFor={`ticket-${site.id}`}>
              <input
                id={`ticket-${site.id}`}
                type="url"
                placeholder="https://…/api/chat/ticket"
                value={site.ticketWebhookUrl ?? ""}
                disabled={!isAdmin}
                onBlur={(e) => void saveWebhook(site, e.currentTarget.value)}
              />
            </SettingRow>
          </>
        ),
      },
      {
        id: "keys",
        title: t.tabKeys,
        intro: t.keysIntro,
        body: (
          <>
            <SecretRow
              label={t.secretKey}
              hint={t.secretKeyHint}
              shown={`sk_…${site.secretKeyHint}`}
              previousUntil={site.previousKeyValidUntil}
              isAdmin={isAdmin}
              lang={lang}
              onRotate={(leaked) => void rotate(site, "identity", leaked)}
              onRevokePrevious={() => void revokePrevious(site, "identity")}
            />
            <SecretRow
              label={t.webhookSecret}
              hint={t.webhookSecretHint}
              shown={site.webhookSecretHint ? `whsec_…${site.webhookSecretHint}` : null}
              previousUntil={site.previousWebhookSecretValidUntil}
              isAdmin={isAdmin}
              lang={lang}
              onRotate={(leaked) => void rotate(site, "webhook", leaked)}
              onRevokePrevious={() => void revokePrevious(site, "webhook")}
            />
          </>
        ),
      },
      {
        id: "protection",
        title: t.tabProtection,
        body: (
          <>
            <SettingRow title={t.identity} hint={t.identityHint}>
              <div class="stack">
                <Toggle
                  checked={site.requireIdentityVerification}
                  disabled={!isAdmin || (!canRequireIdentity && !site.requireIdentityVerification)}
                  label={t.identity}
                  onChange={(next) =>
                    void api
                      .updateSite(site.id, {
                        requireIdentityVerification: next,
                      })
                      .then(reload)
                      .catch(report)
                  }
                />
                {canRequireIdentity ? null : <FeatureHint feature="verifiedIdentity" />}
              </div>
            </SettingRow>
            <SettingRow title={t.anonTitle} hint={t.anonIntro} wide>
              <AnonLimits
                site={site}
                isAdmin={isAdmin}
                onSaved={async () => {
                  show("ok", t.anonSaved(site.name));
                  await reload();
                }}
                onError={report}
              />
            </SettingRow>
          </>
        ),
      },
    ];
    // Last, apart, and the owner's alone: switching a site off under "General"
    // is the everyday answer; this one cannot be taken back.
    if (isAdmin) {
      groups.push({
        id: "danger",
        title: t.tabDanger,
        body: (
          <div class="danger-box">
            <div>
              <strong>{t.deleteTitle}</strong>
              <p class="hint">{isOwner ? t.deleteIntro : t.deleteOwnerOnly}</p>
            </div>
            {isOwner ? (
              <button class="btn danger" onClick={() => void removeSite(site)}>
                {t.deleteButton}
              </button>
            ) : null}
          </div>
        ),
      });
    }
    return groups;
  }

  const header = (
    <div class="sites-top">
      <div>
        <p class="sites-count">{t.siteCount(sites.length)}</p>
      </div>
      <div class="sites-top-actions">
        {isAdmin ? (
          <button class="btn primary" onClick={() => setCreating(true)}>
            + {t.newButton}
          </button>
        ) : null}
      </div>
    </div>
  );

  let body: JSX.Element;
  if (sites.length === 0) {
    body = (
      <div class="panel sites-empty">
        <h2>{t.emptyTitle}</h2>
        <p class="intro">{t.newIntro}</p>
        {isAdmin ? (
          <button class="btn primary" onClick={() => setCreating(true)}>
            + {t.newButton}
          </button>
        ) : null}
      </div>
    );
  } else {
    body = <SiteSettings sites={sites} selected={selected!} onSelect={setSelectedId} sectionsOf={sectionsOf} />;
  }

  return (
    <div class="sites-page">
      <ReadOnlyNote isAdmin={isAdmin} />
      {error ? <div class="alert error">{error}</div> : null}
      <PlanLimitNotice limit={limit} />
      <StatusLine status={status} />
      {dialogs.dialog}
      {header}
      {body}
      {creating ? <NewSiteDialog onClose={() => setCreating(false)} onCreate={createSite} /> : null}
      {revealed ? <RevealedDialog revealed={revealed} onClose={() => setRevealed(null)} /> : null}
    </div>
  );
}

/* ------------------------------------------------------------ layout */

type SectionsOf = (site: Site) => Array<{ id: SectionId; title: string; intro?: string; body: JSX.Element }>;

/**
 * The sites as a strip of cards, the chosen one below it, one group at a time:
 * the least scrolling, and a setting is always under the same tab.
 */
function SiteSettings({
  sites,
  selected,
  onSelect,
  sectionsOf,
}: {
  sites: Site[];
  selected: Site;
  onSelect: (id: number) => void;
  sectionsOf: SectionsOf;
}): JSX.Element {
  const [tab, setTab] = useState<SectionId>("general");
  const sections = sectionsOf(selected);
  const current = sections.find((s) => s.id === tab) ?? sections[0]!;
  return (
    <>
      <div class="sites-strip" role="tablist">
        {sites.map((site) => (
          <button
            key={site.id}
            role="tab"
            aria-selected={site.id === selected.id}
            class="sites-chipcard"
            onClick={() => onSelect(site.id)}
          >
            <SiteMark site={site} />
            <span class="sites-chipcard-text">
              <strong>{site.name}</strong>
              <SiteMeta site={site} />
            </span>
          </button>
        ))}
      </div>
      <section class="panel sites-detail">
        <SiteHead site={selected} />
        <div class="tabs-line" role="tablist">
          {sections.map((s) => (
            <button
              key={s.id}
              role="tab"
              aria-selected={s.id === current.id}
              class={s.id === "danger" ? "danger" : ""}
              onClick={() => setTab(s.id)}
            >
              {s.title}
            </button>
          ))}
        </div>
        <div class="sites-body">
          {current.intro ? <p class="intro">{current.intro}</p> : null}
          {current.body}
        </div>
      </section>
    </>
  );
}

/* ------------------------------------------------------------ small pieces */

/** A site's monogram, tinted by its widget colour so sites tell apart at a glance. */
function SiteMark({ site }: { site: Site }): JSX.Element {
  const colour = site.widgetConfig?.primaryColor ?? "#0d9488";
  return (
    <span
      class="sites-mark"
      style={{
        background: colour,
        color: site.widgetConfig?.onPrimaryColor ?? "#fff",
      }}
      aria-hidden="true"
    >
      {site.name.trim().charAt(0).toUpperCase() || "?"}
    </span>
  );
}

function StatusPill({ site }: { site: Site }): JSX.Element {
  const { t: all } = useI18n();
  const t = all.sites;
  if (!site.isActive) return <span class="pill off">{t.statusOff}</span>;
  if (needsOrigins(site)) return <span class="pill warn">{t.noOriginsBadge}</span>;
  return <span class="pill on">{t.statusOn}</span>;
}

function SiteMeta({ site }: { site: Site }): JSX.Element {
  const { t: all } = useI18n();
  const t = all.sites;
  const first = site.allowedOrigins[0]?.replace(/^https?:\/\//, "");
  return (
    <span class="sites-meta">
      {first ?? t.noOriginsBadge}
      {site.allowedOrigins.length > 1 ? ` +${site.allowedOrigins.length - 1}` : ""}
    </span>
  );
}

function DomainChips({ origins }: { origins: string[] }): JSX.Element {
  return (
    <span class="sites-domains">
      {origins.slice(0, 3).map((o) => (
        <span key={o} class="chip">
          {o.replace(/^https?:\/\//, "")}
        </span>
      ))}
      {origins.length > 3 ? <span class="chip">+{origins.length - 3}</span> : null}
    </span>
  );
}

/** The selected site at the top of its settings: who it is and whether it is live. */
function SiteHead({ site }: { site: Site }): JSX.Element {
  const { t: all } = useI18n();
  const t = all.sites;
  return (
    <div class="sites-head">
      <SiteMark site={site} />
      <div class="sites-head-text">
        <h2>{site.name}</h2>
        <div class="sites-head-meta">
          <StatusPill site={site} />
          {site.allowedOrigins.length ? <DomainChips origins={site.allowedOrigins} /> : null}
        </div>
      </div>
      <div class="sites-head-key">
        <span class="hint">{t.publicKey}</span>
        <CopyBox value={site.publicKey} compact />
      </div>
    </div>
  );
}

/**
 * How full this site's attachment storage is.
 *
 * The cap existed before this line did, and the only person it ever spoke to
 * was a visitor being told their screenshot would not go through — which
 * reaches the owner, if at all, as a complaint. Past four fifths it is said
 * loudly, while deleting a few old conversations still fixes it.
 */
function StorageRow({ storage, lang }: { storage: Site["storage"]; lang: "tr" | "en" }): JSX.Element | null {
  const { t: all } = useI18n();
  const t = all.sites;
  // Null on a response that did not pay for the aggregate.
  if (!storage) return null;
  const { percent, warn } = storageUse(storage.usedBytes, storage.capBytes);
  return (
    <SettingRow title={t.storage} hint={t.storageHint}>
      <div class="stack">
        <div class={`meter${warn ? " warn" : ""}`}>
          <span style={{ width: `${Math.max(2, percent)}%` }} />
        </div>
        <span class={warn ? "alert warn" : "hint"}>
          {t.storageUsed(bytes(storage.usedBytes, lang), bytes(storage.capBytes, lang), percent)}
          {warn ? t.storageWarn : ""}
        </span>
      </div>
    </SettingRow>
  );
}

/**
 * One secret: its hint, whether a replaced value is still live, and the two
 * ways to rotate it. `shown` is null for a webhook key that was never made.
 */
function SecretRow({
  label,
  hint,
  shown,
  previousUntil,
  isAdmin,
  lang,
  onRotate,
  onRevokePrevious,
}: {
  label: string;
  hint: string;
  shown: string | null;
  previousUntil: string | null;
  isAdmin: boolean;
  lang: "tr" | "en";
  onRotate: (leaked: boolean) => void;
  onRevokePrevious: () => void;
}): JSX.Element {
  const { t: all } = useI18n();
  const t = all.sites;
  return (
    <SettingRow title={label} hint={hint}>
      <div class="stack">
        {shown ? <code class="sites-secret">{shown}</code> : <p class="hint">{t.webhookMissing}</p>}
        {previousUntil ? (
          <div class="alert warn key-previous">
            {t.previousValid(dateTime(previousUntil, lang))}
            {isAdmin ? (
              <button class="btn sm danger" onClick={onRevokePrevious}>
                {t.revokePrevious}
              </button>
            ) : null}
          </div>
        ) : null}
        {isAdmin ? (
          <div class="actions-row">
            {shown ? (
              <>
                <button class="btn sm" onClick={() => onRotate(false)}>
                  {t.rotate}
                </button>
                <button class="btn sm danger" onClick={() => onRotate(true)}>
                  {t.rotateLeaked}
                </button>
              </>
            ) : (
              <button class="btn sm primary" onClick={() => onRotate(false)}>
                {t.webhookCreate}
              </button>
            )}
          </div>
        ) : null}
      </div>
    </SettingRow>
  );
}

/** The anonymous budget fields. Saved together, validated before sending. */
function AnonLimits({
  site,
  isAdmin,
  onSaved,
  onError,
}: {
  site: Site;
  isAdmin: boolean;
  onSaved: () => Promise<void>;
  onError: (err: unknown) => void;
}): JSX.Element {
  const { t: all } = useI18n();
  const t = all.sites;
  const initial = {
    visitors: String(site.anonVisitorLimit),
    conversations: String(site.anonConversationLimit),
    window: String(site.anonWindowMinutes),
  };
  const [form, setForm] = useState(initial);
  const [invalid, setInvalid] = useState(false);
  // A reload after saving (or another tab's change) brings new values in.
  useEffect(() => {
    setForm(initial);
  }, [site.anonVisitorLimit, site.anonConversationLimit, site.anonWindowMinutes]);

  const dirty =
    form.visitors !== initial.visitors ||
    form.conversations !== initial.conversations ||
    form.window !== initial.window;

  async function save(event: Event): Promise<void> {
    event.preventDefault();
    const whole = (value: string, min: number, max: number): number | null => {
      if (!/^\d+$/.test(value.trim())) return null;
      const n = Number(value);
      return n >= min && n <= max ? n : null;
    };
    const visitors = whole(form.visitors, 0, 1_000_000);
    const conversations = whole(form.conversations, 0, 100_000);
    const windowMinutes = whole(form.window, 1, 1440);
    if (visitors === null || conversations === null || windowMinutes === null) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    try {
      await api.updateSite(site.id, {
        anonVisitorLimit: visitors,
        anonConversationLimit: conversations,
        anonWindowMinutes: windowMinutes,
      });
      await onSaved();
    } catch (err) {
      onError(err);
    }
  }

  return (
    <form class="sites-anon" onSubmit={(e) => void save(e)}>
      <div class="grid-3">
        <label>
          {t.anonVisitors}
          <input
            type="number"
            min={0}
            value={form.visitors}
            disabled={!isAdmin}
            onInput={(e) => setForm({ ...form, visitors: e.currentTarget.value })}
          />
        </label>
        <label>
          {t.anonConversations}
          <input
            type="number"
            min={0}
            value={form.conversations}
            disabled={!isAdmin}
            onInput={(e) => setForm({ ...form, conversations: e.currentTarget.value })}
          />
        </label>
        <label>
          {t.anonWindow}
          <input
            type="number"
            min={1}
            max={1440}
            value={form.window}
            disabled={!isAdmin}
            onInput={(e) => setForm({ ...form, window: e.currentTarget.value })}
          />
        </label>
      </div>
      {invalid ? <p class="field-error">{t.anonInvalid}</p> : null}
      {isAdmin ? (
        <div class="actions-row">
          <button class="btn sm primary" disabled={!dirty}>
            {all.common.save}
          </button>
        </div>
      ) : null}
    </form>
  );
}

/** Adding a site: a name and its domains, in a window of its own rather than at the foot of the page. */
function NewSiteDialog({
  onClose,
  onCreate,
}: {
  onClose: () => void;
  onCreate: (name: string, origins: string) => Promise<boolean>;
}): JSX.Element {
  const { t: all } = useI18n();
  const t = all.sites;
  const [name, setName] = useState("");
  const [origins, setOrigins] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div class="dialog-scrim" onClick={onClose}>
      <form
        class="dialog form-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-site-title"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim() || busy) return;
          setBusy(true);
          void onCreate(name, origins).then((ok) => {
            setBusy(false);
            if (ok) onClose();
          });
        }}
      >
        <h2 id="new-site-title">{t.newTitle}</h2>
        <p class="dialog-body">{t.newIntro}</p>
        <label>
          {t.name}
          <input
            type="text"
            value={name}
            maxLength={120}
            autoFocus
            onInput={(e) => setName(e.currentTarget.value)}
            required
          />
        </label>
        <label>
          <span>
            {t.origins} <span class="hint">{t.originsHint}</span>
          </span>
          <textarea
            class="plain"
            rows={3}
            placeholder="https://siteniz.com"
            value={origins}
            onInput={(e) => setOrigins(e.currentTarget.value)}
          />
        </label>
        {parseOrigins(origins).length === 0 ? <p class="hint">{t.noOriginsNewHint}</p> : null}
        <div class="dialog-actions">
          <button type="button" class="btn" onClick={onClose}>
            {all.common.cancel}
          </button>
          <button class="btn primary" disabled={!name.trim() || busy}>
            {t.create}
          </button>
        </div>
      </form>
    </div>
  );
}

/** Secrets shown once: in front of everything, each with a copy button, until the admin says they have them. */
function RevealedDialog({ revealed, onClose }: { revealed: Revealed; onClose: () => void }): JSX.Element {
  const { t: all } = useI18n();
  const t = all.sites;
  return (
    <div class="dialog-scrim">
      <div class="dialog form-dialog wide" role="alertdialog" aria-modal="true" aria-labelledby="revealed-title">
        <h2 id="revealed-title">{revealed.title}</h2>
        <div class="alert warn">{revealed.warn}</div>
        {revealed.items.map((item) => (
          <div key={item.label} class="stack">
            <span class="setting-title">{item.label}</span>
            <CopyBox value={item.value} />
          </div>
        ))}
        <div class="dialog-actions">
          <button class="btn primary" onClick={onClose}>
            {t.savedHide}
          </button>
        </div>
      </div>
    </div>
  );
}
